import React from 'react';
import { Form as FinalForm, Field } from 'react-final-form';

import { FormattedMessage } from '../../util/reactIntl';
import { Button, FieldTextInput, Form } from '../../components';
import { PAYMENT_METHODS } from '../../constants/payment.constants';

import css from './CreateListingDurationForm.module.css';

export const DURATION_OPTIONS = [
  {
    value: PAYMENT_METHODS.PRIVATE.MONTHLY,
    titleKey: 'General.paymentMethods.privateSingleMonthly',
    taglineKey: 'CreateListingDurationPage.option.monthly.tagline',
    priceTextKey: 'CreateListingDurationPage.option.monthly.priceText',
    isMostChosen: false,
  },
  {
    value: PAYMENT_METHODS.PRIVATE.QUARTERLY,
    titleKey: 'General.paymentMethods.privateSingleQuarterly',
    taglineKey: 'CreateListingDurationPage.option.quarterly.tagline',
    priceTextKey: 'CreateListingDurationPage.option.quarterly.priceText',
    isMostChosen: true,
  },
  {
    value: PAYMENT_METHODS.PRIVATE.SEMI_ANNUAL,
    titleKey: 'General.paymentMethods.privateSingleSemiAnnual',
    taglineKey: 'CreateListingDurationPage.option.semiAnnual.tagline',
    priceTextKey: 'CreateListingDurationPage.option.semiAnnual.priceText',
    isMostChosen: false,
  },
];

const CreateListingDurationFormComponent = ({ handleSubmit, values, inProgress, stepIndicator }) => {
  const selectedOption = DURATION_OPTIONS.find(o => o.value === values.plan);

  return (
    <Form onSubmit={handleSubmit} className={css.root}>
      {/* Only the wizard is a numbered flow. Renewing an expired advertisement uses this
          same form but is not "step 1 of anything", and the old hardcoded "(step 1 of 3)"
          was wrong in both places once payment moved inside the wizard. */}
      {stepIndicator ? <p className={css.stepIndicator}>{stepIndicator}</p> : null}
      <h1 className={css.heading}>
        <FormattedMessage id="CreateListingDurationPage.heading" />
      </h1>

      <div className={css.optionsList}>
        {DURATION_OPTIONS.map(({ value, titleKey, taglineKey, priceTextKey, isMostChosen }) => (
          <Field key={value} name="plan" type="radio" value={value}>
            {({ input }) => (
              <label
                className={`${css.optionCard} ${input.checked ? css.optionCardSelected : ''}`}
                htmlFor={`duration-${value}`}
              >
                <input {...input} id={`duration-${value}`} className={css.radioInput} />
                <div className={css.optionHeader}>
                  <div className={css.optionLeft}>
                    <span
                      className={`${css.radioCircle} ${
                        input.checked ? css.radioCircleChecked : ''
                      }`}
                    />
                    <span className={css.optionTitle}>
                      <FormattedMessage id={titleKey} />
                    </span>
                  </div>
                  {isMostChosen && (
                    <span className={css.mostChosen}>
                      <FormattedMessage id="CreateListingDurationPage.mostChosen" />
                    </span>
                  )}
                </div>
                <p className={css.tagline}>
                  <FormattedMessage id={taglineKey} />
                </p>
                <p className={css.priceText}>
                  <FormattedMessage id={priceTextKey} />
                </p>
              </label>
            )}
          </Field>
        ))}
      </div>

      {/* The prices above are BTW-inclusive — Stripe splits each one into net + 21%
          on the invoice, so the seller pays exactly what is quoted. Saying so here
          is what makes the quoted figure a complete price rather than an ambiguous one. */}
      <p className={css.vatIncluded}>
        <FormattedMessage id="CreateListingDurationPage.vatIncluded" />
      </p>

      <p className={css.autoRenewal}>
        <FormattedMessage id="CreateListingDurationPage.autoRenewal" />
      </p>

      {selectedOption && (
        <p className={css.summary}>
          <FormattedMessage
            id="CreateListingDurationPage.summary"
            values={{
              weeks: (
                <strong>
                  <FormattedMessage id={selectedOption.titleKey} />
                </strong>
              ),
            }}
          />
        </p>
      )}

      {/* `inProgress` only renders the spinner — Button computes `disabled` from the
          `disabled` prop alone, so it must be included here or a second click
          creates a second checkout session. */}
      <Button
        className={css.ctaButton}
        type="submit"
        inProgress={inProgress}
        disabled={!values.plan || inProgress}
      >
        <FormattedMessage id="CreateListingDurationPage.ctaButton" />
      </Button>

      <p className={css.securePayment}>
        <FormattedMessage id="CreateListingDurationPage.securePayment" />
      </p>

      <div className={css.promoCodeWrapper}>
        <label htmlFor="promoCode" className={css.promoCodeLabel}>
          <FormattedMessage id="CreateListingDurationPage.promoCodeLabel" />
          <span className={css.promoCodeOptional}>
            {' '}
            <FormattedMessage id="CreateListingDurationPage.promoCodeOptional" />
          </span>
        </label>
        <FieldTextInput id="promoCode" name="promoCode" className={css.promoCodeInput} />
      </div>

      <div className={css.bottomLinks}>
        <a
          href="https://www.vaarmarkt.nl/adverteren-op-vaarmarkt/zakelijk-verkopen/"
          className={css.businessLink}
        >
          <FormattedMessage id="ProfileSettingsForm.businessLink" />
        </a>
      </div>
    </Form>
  );
};

const CreateListingDurationForm = ({ initialValues, onSubmit, inProgress, stepIndicator }) => (
  <FinalForm
    initialValues={initialValues}
    onSubmit={onSubmit}
    render={formProps => (
      <CreateListingDurationFormComponent
        {...formProps}
        inProgress={inProgress}
        stepIndicator={stepIndicator}
      />
    )}
  />
);

export default CreateListingDurationForm;
